import type { DealDocument } from '@/services/documents'
import type { DealCorrespondenceFile } from '@/services/dossierContext'
import type { Trigger } from '@/types'

export type DocumentStatus = 'unclassified' | 'received' | 'pending' | 'expired' | 'approved' | 'rejected'
export type DocumentTab = 'all' | 'required' | DocumentStatus

/**
 * Where a row's file actually lives.
 *
 *   vault   uploaded to the deal; versioned, revisable, downloaded by id
 *   intake  an original stored with the package the deal arrived in;
 *           immutable, addressed by content hash, read through the source
 *           viewer
 *
 * Both are documents on this deal and belong in one list. The distinction is
 * carried on the row rather than erased, because a reader has to be able to
 * tell evidence of what arrived from a file someone uploaded afterwards.
 */
export type DocumentOrigin = 'vault' | 'intake'

export type DocumentRow = {
  document: DealDocument
  origin: DocumentOrigin
  /** Content hash for an intake original — how the source viewer addresses it. */
  sourceSha256: string | null
  /** The intake record the original arrived on. A deal has several: one per
   *  message in the conversation, and a file is addressed through its own. */
  sourceIngestionId?: string | null
  /** Whether this row's bytes can actually be opened. False for an attachment
   *  the parser saw but could not store: it arrived, so it is listed, but
   *  offering a download that cannot succeed is worse than saying so. */
  available: boolean
  status: DocumentStatus
  statusRecorded: boolean
  documentType: string | null
  subcategory: string | null
  party: string | null
  required: boolean
  requirementId: string | null
  requirement: Trigger | null
  effectiveDate: string | null
  expirationDate: string | null
  source: string | null
  pages: number | null
  tags: string[]
  reviewedBy: string | null
  reviewedOn: string | null
}

export type DocumentFilters = {
  tab: DocumentTab
  search: string
  category: string
  documentType: string
  party: string
  status: string
}

export type DocumentDashboardModel = {
  rows: DocumentRow[]
  /** Every stored *vault* version, newest first — the pool version history is
   *  drawn from. Intake originals are deliberately absent: they have no
   *  versions, and letting one into this pool would hand its bytes to any
   *  vault document that happens to share its filename. */
  allVersions: DealDocument[]
  totalDocuments: number
  /** Files held for this deal, superseded vault versions and intake originals
   *  included. Larger than `allVersions.length` when the deal arrived with a
   *  package — see above for why the two are not the same list. */
  totalVersions: number
  requiredCount: number
  resolvedRequirementCount: number
  openRequirementCount: number
  overdueRequirementCount: number
  approvedCount: number | null
  requirementProgressPct: number | null
  documentRequirements: Trigger[]
  categories: string[]
  documentTypes: string[]
  parties: string[]
  statuses: DocumentStatus[]
  tabCounts: Record<DocumentTab, number>
}

function textValue(fields: Record<string, unknown>, keys: string[]): string | null {
  for (const key of keys) {
    const value = fields[key]
    if (typeof value === 'string' && value.trim()) return value.trim()
  }
  return null
}

function booleanValue(fields: Record<string, unknown>, keys: string[]): boolean | null {
  for (const key of keys) {
    const value = fields[key]
    if (typeof value === 'boolean') return value
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase()
      if (['yes', 'true', 'required'].includes(normalized)) return true
      if (['no', 'false', 'optional'].includes(normalized)) return false
    }
  }
  return null
}

function numberValue(fields: Record<string, unknown>, keys: string[]): number | null {
  for (const key of keys) {
    const value = fields[key]
    if (typeof value === 'number' && Number.isFinite(value)) return value
    if (typeof value === 'string' && value.trim() && Number.isFinite(Number(value))) return Number(value)
  }
  return null
}

function stringArray(fields: Record<string, unknown>, keys: string[]): string[] {
  for (const key of keys) {
    const value = fields[key]
    if (Array.isArray(value)) {
      return value.filter((item): item is string => typeof item === 'string' && Boolean(item.trim())).map((item) => item.trim())
    }
    if (typeof value === 'string' && value.trim()) {
      return value.split(',').map((item) => item.trim()).filter(Boolean)
    }
  }
  return []
}

function normalizeStatus(value: string | null): { status: DocumentStatus; recorded: boolean } {
  if (!value) return { status: 'unclassified', recorded: false }
  const normalized = value.trim().toLowerCase().replace(/[\s-]+/g, '_')
  if (['received', 'uploaded', 'stored'].includes(normalized)) return { status: 'received', recorded: true }
  if (['pending', 'in_review', 'under_review'].includes(normalized)) return { status: 'pending', recorded: true }
  if (['expired', 'overdue'].includes(normalized)) return { status: 'expired', recorded: true }
  if (['approved', 'accepted'].includes(normalized)) return { status: 'approved', recorded: true }
  if (['rejected', 'declined'].includes(normalized)) return { status: 'rejected', recorded: true }
  return { status: 'unclassified', recorded: false }
}

function createdTime(document: DealDocument): number {
  const parsed = new Date(document.createdAt).getTime()
  return Number.isNaN(parsed) ? 0 : parsed
}

function currentDocuments(documents: DealDocument[]): DealDocument[] {
  const current = new Map<string, DealDocument>()
  for (const document of documents) {
    // The API allocates versions by exact filename; case/whitespace variants
    // are separate backend lineages and must remain separate here.
    const key = document.filename
    const existing = current.get(key)
    if (!existing || document.version > existing.version || (document.version === existing.version && createdTime(document) > createdTime(existing))) {
      current.set(key, document)
    }
  }
  return [...current.values()].sort((a, b) => createdTime(b) - createdTime(a))
}

function explicitDocumentRequirement(trigger: Trigger, referencedIds: Set<string>): boolean {
  if (referencedIds.has(trigger.id)) return true
  const type = trigger.type.trim().toLowerCase().replace(/[_-]+/g, ' ')
  return type === 'document' || type === 'document requirement' || type === 'required document' || type === 'document request'
}

function rowFromDocument(document: DealDocument, triggersById: Map<string, Trigger>): DocumentRow {
  const fields = document.extractedFields ?? {}
  const requirementId = textValue(fields, ['requirementId', 'documentRequirementId'])
  const requirement = requirementId ? triggersById.get(requirementId) ?? null : null
  const explicitRequired = booleanValue(fields, ['required', 'isRequired', 'documentRequired'])
  // A generic extracted `status` is not evidence of document workflow state.
  const explicitStatuses = [
    normalizeStatus(textValue(fields, ['documentStatus'])),
    normalizeStatus(textValue(fields, ['reviewStatus'])),
  ]
  const { status, recorded } = explicitStatuses.find((candidate) => candidate.recorded)
    ?? { status: 'unclassified' as const, recorded: false }
  const documentType = textValue(fields, ['documentType', 'type', 'subcategory'])

  return {
    document,
    origin: 'vault',
    sourceSha256: document.checksumSha256 ?? null,
    available: true,
    status,
    statusRecorded: recorded,
    documentType,
    subcategory: textValue(fields, ['subcategory']) ?? documentType,
    party: textValue(fields, ['party', 'documentParty', 'responsibleParty']),
    required: explicitRequired === true || Boolean(requirement),
    requirementId,
    requirement,
    effectiveDate: textValue(fields, ['effectiveDate']),
    expirationDate: textValue(fields, ['expirationDate', 'expiryDate']),
    source: textValue(fields, ['source', 'documentSource']),
    pages: numberValue(fields, ['pages', 'pageCount']),
    tags: stringArray(fields, ['tags']),
    reviewedBy: textValue(fields, ['reviewedBy', 'reviewer']),
    reviewedOn: textValue(fields, ['reviewedOn', 'reviewedAt']),
  }
}


/** What the Documents page knows about the package a deal arrived on.
 *
 *  `files` is what the API hands back for opening a stored original; the sizes
 *  live on the ingestion record's attachment list instead, keyed by the same
 *  content hash, so the caller joins them here rather than the table showing a
 *  dash for a number the package actually recorded. */
export type IntakeOriginals = {
  /** Every file that arrived on the deal, each naming its own message. */
  files: DealCorrespondenceFile[]
}

/** The intake originals, as rows for the one document list.
 *
 *  A `SourceFileRef` carries only a filename, a kind and a content hash, so
 *  everything the table cannot know stays empty rather than being invented:
 *  no uploader, no size, no version history, and a date only when the package
 *  recorded one. `INTAKE_ROW_PREFIX` keeps the synthetic ids in their own
 *  namespace so one can never collide with a vault document id.
 *
 *  A source file already promoted into the vault is NOT listed twice. The
 *  vault copy wins, because it is the one that versions — a duplicate row for
 *  the same bytes is the double-count this list exists to avoid. */
export const INTAKE_ROW_PREFIX = 'intake:'

function intakeRow(file: DealCorrespondenceFile): DocumentRow {
  const isEmail = file.kind === 'email_source'
  // Who it came from, as a reader would cite it: the server names the broker
  // when the envelope's sender is the lender's own forwarder; the sender
  // otherwise.
  const sender = file.party ?? file.fromName ?? file.from
  return {
    document: {
      // An unstored attachment has no content hash to be addressed by; the
      // message it arrived on plus its name still identify the row.
      id: `${INTAKE_ROW_PREFIX}${file.sha256 ?? `${file.ingestionId}:${file.filename}`}`,
      loanId: null,
      filename: file.filename,
      contentType: isEmail ? 'message/rfc822' : null,
      // 0 when nothing recorded a size; the page shows a dash for it rather
      // than claiming an empty file.
      sizeBytes: file.sizeBytes ?? 0,
      // What the parser determined the file is, so the Category filter can
      // separate a term sheet from an offering memorandum instead of showing
      // one undifferentiated pile of correspondence.
      category: file.category || 'correspondence',
      version: 1,
      checksumSha256: file.sha256,
      uploadedBy: null,
      notes: null,
      // The date of the message THIS file arrived on, not of the deal's first
      // message — which is what makes the list sort through the conversation.
      createdAt: file.receivedAt ?? '',
    },
    origin: 'intake',
    sourceSha256: file.sha256,
    sourceIngestionId: file.ingestionId,
    available: file.available !== false,
    // "Received" is a workflow state a person sets. What is recorded here is
    // that the file arrived on a message, which is not the same claim.
    status: 'unclassified',
    statusRecorded: false,
    documentType: file.documentType,
    subcategory: null,
    party: sender,
    required: false,
    requirementId: null,
    requirement: null,
    effectiveDate: null,
    expirationDate: null,
    // The message it came in on — the answer to "where did this come from".
    source: file.subject ? `Email: ${file.subject}` : 'Intake correspondence',
    pages: file.pages ?? null,
    tags: [],
    reviewedBy: null,
    reviewedOn: null,
  }
}

export function buildDocumentsModel(
  documents: DealDocument[],
  triggers: Trigger[],
  /** Originals stored with the package the deal arrived in. They are documents
   *  on this deal, so they belong in this list beside the uploaded ones. */
  intake?: IntakeOriginals,
  /** Files the diligence checklist accepted for a REQUIRED item. The table's
   *  Required tab read 0 beside a checklist of 17 required documents received,
   *  because only an explicit trigger link counted (stress test DJA-12). */
  checklistRequiredFiles?: ReadonlySet<string>,
): DocumentDashboardModel {
  const allVersions = [...documents].sort((a, b) => createdTime(b) - createdTime(a))
  const triggersById = new Map(triggers.map((trigger) => [trigger.id, trigger]))
  const vaultRows = currentDocuments(documents).map((document) => rowFromDocument(document, triggersById))
  // A source file that has been promoted into the vault is already listed.
  const vaultHashes = new Set(
    documents.map((document) => document.checksumSha256).filter((hash): hash is string => Boolean(hash)),
  )
  const seenSourceHashes = new Set<string>()
  const seenUnhashed = new Set<string>()
  const intakeRows = (intake?.files ?? []).flatMap((file) => {
    if (file.sha256) {
      if (vaultHashes.has(file.sha256) || seenSourceHashes.has(file.sha256)) return []
      seenSourceHashes.add(file.sha256)
      return [intakeRow(file)]
    }
    // No content hash means the bytes were never stored — which is precisely
    // the file this list must not drop. It cannot be deduped by content, so
    // the message it arrived on and its name identify it instead.
    const key = `${file.ingestionId}:${file.filename}`
    if (seenUnhashed.has(key)) return []
    seenUnhashed.add(key)
    return [intakeRow(file)]
  })
  // One list, newest first. Vault uploads and correspondence interleave by
  // date rather than sitting in separate blocks: a reader looking for the
  // latest thing on a deal should not have to know which store it landed in.
  // An undated row sorts last — it cannot claim to be recent.
  const rows = [...vaultRows, ...intakeRows].map((row) =>
    !row.required && checklistRequiredFiles?.has(row.document.filename) ? { ...row, required: true } : row,
  ).sort((a, b) => {
    const at = createdTime(a.document)
    const bt = createdTime(b.document)
    if (at === bt) return a.document.filename.localeCompare(b.document.filename)
    return bt - at
  })
  const referencedRequirementIds = new Set(rows.map((row) => row.requirementId).filter((id): id is string => Boolean(id)))
  const documentRequirements = triggers.filter((trigger) => explicitDocumentRequirement(trigger, referencedRequirementIds))
  const linkedRequirementIds = new Set(documentRequirements.map((trigger) => trigger.id))
  const unlinkedRequiredDocuments = rows.filter((row) => row.required && (!row.requirementId || !linkedRequirementIds.has(row.requirementId)))
  const resolvedRequirements = documentRequirements.filter((trigger) => trigger.status === 'resolved').length + unlinkedRequiredDocuments.length
  const openRequirements = documentRequirements.filter((trigger) => trigger.status === 'pending' || trigger.status === 'upcoming').length
  const overdueRequirements = documentRequirements.filter((trigger) => trigger.status === 'overdue').length
  const requiredCount = documentRequirements.length + unlinkedRequiredDocuments.length
  const statusRecorded = rows.some((row) => row.statusRecorded)

  const statuses = [...new Set(rows.map((row) => row.status))].sort()
  const tabCounts: Record<DocumentTab, number> = {
    all: rows.length,
    required: rows.filter((row) => row.required).length,
    unclassified: rows.filter((row) => row.status === 'unclassified').length,
    received: rows.filter((row) => row.status === 'received').length,
    pending: rows.filter((row) => row.status === 'pending').length,
    expired: rows.filter((row) => row.status === 'expired').length,
    approved: rows.filter((row) => row.status === 'approved').length,
    rejected: rows.filter((row) => row.status === 'rejected').length,
  }

  return {
    rows,
    allVersions,
    totalDocuments: rows.length,
    totalVersions: documents.length + intakeRows.length,
    requiredCount,
    resolvedRequirementCount: resolvedRequirements,
    openRequirementCount: openRequirements,
    overdueRequirementCount: overdueRequirements,
    approvedCount: statusRecorded ? tabCounts.approved : null,
    requirementProgressPct: requiredCount > 0 ? Math.round((resolvedRequirements / requiredCount) * 100) : null,
    documentRequirements,
    categories: [...new Set(rows.map((row) => row.document.category).filter(Boolean))].sort(),
    documentTypes: [...new Set(rows.map((row) => row.documentType).filter((value): value is string => Boolean(value)))].sort(),
    parties: [...new Set(rows.map((row) => row.party).filter((value): value is string => Boolean(value)))].sort(),
    statuses,
    tabCounts,
  }
}

export function filterDocumentRows(rows: DocumentRow[], filters: DocumentFilters): DocumentRow[] {
  const query = filters.search.trim().toLowerCase()
  return rows.filter((row) => {
    if (filters.tab === 'required' && !row.required) return false
    if (filters.tab !== 'all' && filters.tab !== 'required' && row.status !== filters.tab) return false
    if (filters.category && row.document.category !== filters.category) return false
    if (filters.documentType && row.documentType !== filters.documentType) return false
    if (filters.party && row.party !== filters.party) return false
    if (filters.status && row.status !== filters.status) return false
    if (!query) return true
    return [
      row.document.filename,
      row.document.category,
      row.documentType,
      row.party,
      row.document.uploadedBy,
      row.document.notes,
      ...row.tags,
    ].some((value) => String(value ?? '').toLowerCase().includes(query))
  })
}

export function documentVersionHistory(model: DocumentDashboardModel, document: DealDocument): DealDocument[] {
  // An intake original has no versions — it is the file that arrived. Matching
  // on filename alone would hand it the history of an unrelated vault upload
  // that happens to share a name.
  if (document.id.startsWith(INTAKE_ROW_PREFIX)) return []
  return model.allVersions
    .filter((candidate) => candidate.filename === document.filename)
    .sort((a, b) => b.version - a.version || createdTime(b) - createdTime(a))
}


/**
 * Every document on a deal, as one list: the current vault uploads plus the
 * originals the deal arrived with, deduped by content hash.
 *
 * The dossier's other pages each carry a "documents on this deal" panel, and
 * each was passed only the vault. A deal whose files all arrived in its intake
 * package therefore read as having none — Capital Stack said "No documents
 * attached to this deal" about a deal holding three. This is the one answer
 * they should all be asking.
 */
export function dealDocuments(
  documents: DealDocument[],
  intake?: IntakeOriginals,
): DealDocument[] {
  return buildDocumentsModel(documents, [], intake).rows.map((row) => row.document)
}

/** True when a document is an intake original rather than a vault upload. */
export function isIntakeOriginal(document: Pick<DealDocument, 'id'>): boolean {
  return document.id.startsWith(INTAKE_ROW_PREFIX)
}

/** True when nobody uploaded this document: it arrived on an email — an
 *  intake original, or the vault's filing of one (which carries the intake
 *  record's id). "Uploaded by <whoever promoted the record>" was the wrong
 *  sentence for either. */
export function receivedByEmail(document: Pick<DealDocument, 'id' | 'ingestionId'>): boolean {
  return isIntakeOriginal(document) || Boolean(document.ingestionId)
}
